import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import {
  FiatWalletAccountState,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Preloader } from '@sorare/core/src/atoms/loader/Preloader';
import { CreateFiatWalletWithInterstitialModal } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWithInterstitialModal';
import { InterstitialContextModalMode } from '@sorare/core/src/components/fiatWallet/InterstitialContextModal';
import {
  RewardsBanner as DumbBanner,
  RewardsBannerProps as DumbBannerProps,
} from '@sorare/core/src/components/rewards/Banner/Dumb';
import { ShouldVerifyUserBeforeClaiming } from '@sorare/core/src/components/rewards/ShouldVerifyUserBeforeClaiming';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';

import useClaimRewards from 'hooks/so5/useClaimRewards';

import { RewardsBanner_so5Reward } from './__generated__/index.graphql';
import { formatReward } from './utils';

type Props = {
  rewards?: RewardsBanner_so5Reward[];
  renderBanner?: DumbBannerProps['renderBanner'];
  onCloseClaimRewardsDialog?: DumbBannerProps['onCloseClaimRewardsDialog'];
};
export const RewardsBanner = ({
  rewards,
  renderBanner,
  onCloseClaimRewardsDialog,
}: Props) => {
  const { sendUserEngagementIncrementByTier } =
    useSendUserEngagementIncrement();
  const { sport } = useSportContext();
  const [safeGoNextReward, setSafeGoNextReward] = useState<
    (() => void) | undefined
  >(undefined);
  const { fiatWalletAccountable } = useCurrentUserContext();
  const [initialRewards, setInitialRewards] = useState<
    RewardsBanner_so5Reward[] | null
  >(null);
  const [claimRewards, { loading }] = useClaimRewards();

  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);

  const kycCompleted =
    fiatWalletAccountable?.state === FiatWalletAccountState.VALIDATED_OWNER;
  const onClaimFiatRewards = kycCompleted
    ? undefined
    : () => setShowCreateFiatWallet(true);

  if (!rewards) {
    return null;
  }

  if (initialRewards === null && rewards.length > 0) {
    setInitialRewards(rewards);
  }

  const everyInitialRewardsClaimed = initialRewards?.every(
    ({ aasmState }) => aasmState === 'claimed'
  );
  if (!initialRewards || everyInitialRewardsClaimed) {
    return null;
  }

  const onClaim = (ids: string[]) => {
    if (!loading) {
      claimRewards(ids);
    }
  };

  /* Update the aasmState of the initial rewards.
    Two possibilities:
      1. The new rewards array do not contain the claimed rewards, only unclaimed. In this case we manually set the 'aasmState' to 'claimed'
      2. The new rewards array contain all the initial rewards with their 'aasmState' updated
  */
  const updatedRewards = initialRewards.map(initialReward => {
    const updatedReward = rewards.find(r => initialReward.id === r.id);

    return {
      ...initialReward,
      aasmState: updatedReward?.aasmState || 'claimed',
    };
  });

  const cards = initialRewards
    .flatMap(({ rewardCards }) => rewardCards.map(({ anyCard: card }) => card))
    .filter(Boolean);
  const hasBlockchainCardRewards = cards.some(
    card => card!.rarityTyped !== Rarity.common
  );
  const hasProbabilisticBundle = initialRewards.some(
    r => r.probabilisticBundle
  );
  const gameWeek = initialRewards[0]?.so5Fixture.gameWeek;
  const rewardsList = formatReward(updatedRewards, {
    sport,
    safeGoNextReward,
    onClaimFiatRewards,
    sendUserEngagementIncrementByTier,
  });

  return (
    <>
      <Preloader
        imageUrls={cards.map(card => card!.pictureUrl || '')}
        videoUrls={[`${FRONTEND_ASSET_HOST}/animations/coin_reveal.mp4`]}
      />
      <ShouldVerifyUserBeforeClaiming
        hasBlockchainRewards={
          hasBlockchainCardRewards || hasProbabilisticBundle
        }
        trackProperties={{
          gameweek: gameWeek,
        }}
      >
        {({ disabledClaim }) => (
          <>
            <DumbBanner
              disabledClaim={disabledClaim}
              rewards={rewardsList}
              onClaim={onClaim}
              noMargin
              setSafeGoNextReward={setSafeGoNextReward}
              renderBanner={renderBanner}
              onCloseClaimRewardsDialog={onCloseClaimRewardsDialog}
            />
            {showCreateFiatWallet && (
              <CreateFiatWalletWithInterstitialModal
                mode={InterstitialContextModalMode.REWARD}
                onDecline={() => setShowCreateFiatWallet(false)}
                statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
                onDismissActivationSuccess={() =>
                  setShowCreateFiatWallet(false)
                }
                onClose={() => setShowCreateFiatWallet(false)}
                canDismissAfterActivation={false}
              />
            )}
          </>
        )}
      </ShouldVerifyUserBeforeClaiming>
    </>
  );
};

RewardsBanner.fragments = {
  so5Reward: gql`
    fragment RewardsBanner_so5Reward on So5Reward {
      slug
      coinAmount
      rewardCards {
        id
        anyCard {
          slug
          pictureUrl
          rarityTyped
        }
      }
      probabilisticBundle {
        id
      }
      so5Fixture {
        slug
        endDate
        gameWeek
      }
      ...formatReward_so5Reward
    }
    ${formatReward.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsBanner_so5Reward>,
};
